import { useCallback, useEffect, useRef, useState } from 'react'
import { Upload, Image, message } from 'antd'
import type { UploadFile } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { postForm } from '@/utils/request'

const MAX = 6
const MAX_SIZE = 10 * 1024 * 1024

const uploadOne = async (file: File): Promise<string> => {
  const form = new FormData()
  form.append('file', file)
  const res: any = await postForm('users/uploads/image', form)
  if (res?.ok && res.url) return res.url as string
  throw new Error(res?.message || '图片上传失败')
}

/** 反馈附件:多图上传(七牛),受控输出 URL 数组。点选 / 拖拽 / **直接 Ctrl+V 粘贴截图** 都行。
 *  挂载期间监听全局 paste:剪贴板里有图片就拦下来上传;纯文本粘贴不受影响。 */
export const ImageAttach = ({ value, onChange, max = MAX }: { value: string[]; onChange: (urls: string[]) => void; max?: number }) => {
  const [files, setFiles] = useState<UploadFile[]>([])
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // files → 对外 URL 列表(只算上传完成的)
  useEffect(() => {
    onChangeRef.current(files.map((f) => (f.status === 'done' ? (f.response?.url as string) || (f.url as string) : '')).filter(Boolean))
  }, [files])

  // 外部清空(提交成功后 value=[])时同步清空;上传中的不动
  useEffect(() => {
    if (value.length === 0) setFiles((prev) => (prev.length && prev.every((f) => f.status === 'done') ? [] : prev))
  }, [value])

  // 手动上传一批 File(粘贴用):先占位 uploading,完成后回填 url
  const addFiles = useCallback(
    (list: File[]) => {
      setFiles((prev) => {
        const room = Math.max(0, max - prev.length)
        const picked = list.filter((f) => f.type.startsWith('image/')).slice(0, room)
        if (list.length > room) message.warning(`最多 ${max} 张图片`)
        const entries: UploadFile[] = picked.map((f) => ({
          uid: `paste-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: f.name || 'pasted.png',
          status: 'uploading',
          thumbUrl: URL.createObjectURL(f),
        }))
        entries.forEach((entry, i) => {
          const f = picked[i]
          if (f.size > MAX_SIZE) {
            message.error('单张图片不能超过 10MB')
            setFiles((cur) => cur.filter((x) => x.uid !== entry.uid))
            return
          }
          uploadOne(f)
            .then((url) => setFiles((cur) => cur.map((x) => (x.uid === entry.uid ? { ...x, status: 'done', url, response: { url } } : x))))
            .catch((e) => {
              message.error(e?.message || '图片上传失败')
              setFiles((cur) => cur.filter((x) => x.uid !== entry.uid))
            })
        })
        return [...prev, ...entries]
      })
    },
    [max],
  )

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const imgs = Array.from(e.clipboardData?.items || [])
        .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter(Boolean) as File[]
      if (!imgs.length) return
      e.preventDefault()
      addFiles(imgs)
      message.success(`已粘贴 ${imgs.length} 张截图，上传中…`, 1.5)
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [addFiles])

  const customRequest = async (options: any) => {
    try {
      const url = await uploadOne(options.file)
      options.onSuccess?.({ ok: true, url }, options.file)
    } catch (e: any) {
      message.error(e?.message || '图片上传失败')
      options.onError?.(e)
    }
  }

  return (
    <div>
      <Upload
        accept="image/*"
        multiple
        listType="picture-card"
        fileList={files}
        customRequest={customRequest}
        onChange={({ fileList }) => setFiles(fileList.filter((f) => f.status !== 'error'))}
        beforeUpload={(file) => {
          if (file.size > MAX_SIZE) {
            message.error('单张图片不能超过 10MB')
            return Upload.LIST_IGNORE
          }
          return true
        }}
      >
        {files.length >= max ? null : (
          <div className="flex flex-col items-center text-xs text-gray-500">
            <PlusOutlined />
            <div className="mt-1">截图</div>
          </div>
        )}
      </Upload>
      <div className="mt-2 text-xs leading-5" style={{ color: '#86868b' }}>
        截图后直接 Ctrl+V / ⌘V 粘贴即可，也可点击选择或拖入图片
      </div>
    </div>
  )
}

/** 消息里的图片缩略图,点开可预览/左右切换 */
export const MessageImages = ({ images, size = 88 }: { images?: string[]; size?: number }) => {
  if (!images?.length) return null
  return (
    <Image.PreviewGroup>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {images.map((url) => (
          <Image key={url} src={url} width={size} height={size} className="rounded object-cover" style={{ objectFit: 'cover' }} alt="附件" />
        ))}
      </div>
    </Image.PreviewGroup>
  )
}
